import AppContext from 'App/AppContext';
import { IPDefaultButton, IPLabel, IPPrimaryButton } from 'baseComponents';
import { IPDropdown } from 'baseComponents/Dropdown/IPDropdown';
import { IPPanel, IPPanelType } from 'baseComponents/Panel/IPPanel';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { IPTextField } from 'baseComponents/TextField/IPTextField';
import { IPSelectableOption } from 'baseComponents/Utils/SelectableOption';
import TextInfo from 'components/Notification/Info';
import AuthSchemaPicker from 'components/Pickers/AuthSchemaPicker';
import { onRenderPanelHeaderGlobal } from 'globalFunctions';
import {
  archivedIcon,
  globalLayerRightSideBarProps,
  globalStackStylesPanel,
  globalStackStylesPanelFooter,
  globalStackTokensGapLarge,
  globalStackTokensGapSmall,
} from 'globalStyles';
import { TFunction } from 'i18next';
import Joi from 'joi';
import TaskType, { SystemTaskTypes } from 'models/tasks/taskType';
import { FormEvent, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface IEditTaskTypePanel {
  isOpen: boolean;
  onDismiss: () => void;
  onEditTaskType: (item: TaskType) => void;
  onEditForm: (item: TaskType) => void;
  taskTypeToEdit: TaskType;
  // An archived or managed form has no editable metadata, but it still needs a home for the archive
  // action below, so the panel opens with its inputs locked instead of not opening at all.
  metadataReadOnly?: boolean;
  canArchive?: boolean;
  isArchived?: boolean;
  onArchive?: () => void;
}

export const EditTaskTypePanel = (props: IEditTaskTypePanel) => {
  const appContext = useContext(AppContext);
  const { t } = useTranslation(['translation', 'forms']);
  const [error, setError] = useState<Joi.ValidationError | undefined>(undefined);
  const [taskType, setTaskType] = useState<TaskType>(props.taskTypeToEdit.clone());
  const [nameError, setNameError] = useState<string | undefined>(undefined);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    if (props.isOpen) {
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.isOpen, props.taskTypeToEdit]);

  const loadData = async () => {
    try {
      setError(undefined);
      setNameError(undefined);
      setIsLoading(true);
      setTaskType(props.taskTypeToEdit.clone());

      await appContext.globalDataCache.lists.getItems();
    } catch (err) {
      appContext.setError(err);
    } finally {
      setIsLoading(false);
    }
  };

  const _localizedFields: Record<string, string> = {
    name: t('forms:Fields.Name'),
    tabname: t('forms:Fields.Name'),
  };

  const getFieldError = (fieldName: string): string | undefined => {
    if (fieldName === 'name' && nameError) return nameError;

    return error?.details?.find((e) => {
      return e.context?.key === fieldName;
    })?.message;
  };

  const validate = async (fieldName: string, tasktype: TaskType): Promise<string> => {
    const result = tasktype.validate(_localizedFields);
    setError(result.error);

    if (result.error) {
      const fieldError = result.error.details.find((e) => {
        return e.context?.key === fieldName;
      });

      if (fieldError) {
        return fieldError.message;
      }
    }

    if (fieldName === 'name') {
      const existingId = await TaskType.nameExists(tasktype.name.toLowerCase(), appContext);

      if (existingId && existingId !== tasktype.taskTypeId) {
        setNameError(t('forms:Errors.NameError'));
      } else {
        setNameError(undefined);
      }
    }

    return '';
  };

  const hasChanged = (): boolean => {
    return !taskType.isEqual(props.taskTypeToEdit);
  };

  const disableSave = (): boolean => {
    return props.metadataReadOnly || !hasChanged() || error || isLoading || nameError ? true : false;
  };

  const onSave = async () => {
    props.onEditTaskType(taskType);
  };

  // On Render Footer: Save and Cancel at the bottom left, the form-family archive action on the
  // right so a destructive, whole-family action stays apart from the two that only touch this panel.
  const onRenderFooterContent = () => {
    return (
      <IPStack
        horizontal
        wrap
        horizontalAlign="space-between"
        verticalAlign="center"
        styles={globalStackStylesPanelFooter}
        tokens={globalStackTokensGapSmall}
      >
        <IPStack horizontal tokens={globalStackTokensGapSmall}>
          <IPStack.Item>
            <IPPrimaryButton
              disabled={disableSave()}
              onClick={() => {
                onSave();
                props.onDismiss();
              }}
              text={t('translation:General.Button.Save')}
            />
          </IPStack.Item>
          <IPStack.Item>
            <IPDefaultButton onClick={props.onDismiss} text={t('translation:General.Button.Cancel')}></IPDefaultButton>
          </IPStack.Item>
        </IPStack>
        {props.canArchive && props.onArchive && (
          <IPStack.Item>
            <IPDefaultButton
              iconProps={props.isArchived ? { iconName: 'OpenFolderHorizontal' } : archivedIcon}
              text={props.isArchived ? t('forms:Versioning.Unarchive') : t('forms:Versioning.Archive')}
              onClick={props.onArchive}
            />
          </IPStack.Item>
        )}
      </IPStack>
    );
  };

  const getSystemTypeOptions = (): IPSelectableOption[] => {
    const options: IPSelectableOption[] = [
      {
        key: 0,
        text: '',
      },
      {
        key: SystemTaskTypes.Approval,
        text: TaskType.getSystemTypeText(SystemTaskTypes.Approval, t as TFunction<string[]>),
      },
    ];

    return options;
  };

  //
  // Main render
  //
  return (
    <IPPanel
      onRenderHeader={onRenderPanelHeaderGlobal(t('forms:Panels.Edit.Title'), props.taskTypeToEdit.taskTypeId, true)}
      type={IPPanelType.medium}
      isOpen={props.isOpen}
      isBlocking={true}
      isLightDismiss={true}
      layerProps={globalLayerRightSideBarProps}
      isFooterAtBottom={true}
      onRenderFooterContent={onRenderFooterContent}
      onDismiss={props.onDismiss}
    >
      <IPStack verticalFill styles={globalStackStylesPanel} tokens={globalStackTokensGapLarge}>
        <IPStack.Item>
          <TextInfo message={t('forms:Fields.NameInfo')}>
            <IPLabel>{t('forms:Fields.Name')}</IPLabel>
          </TextInfo>
          <IPTextField
            required
            autoFocus
            disabled={props.metadataReadOnly}
            value={taskType.name}
            placeholder={t('forms:Fields.NamePlaceholder')}
            onChange={(ev, newValue) => {
              const newTaskType = taskType.clone();
              newTaskType.name = newValue?.trimStart() ?? '';
              validate('name', newTaskType);
              setTaskType(newTaskType);
            }}
            maxLength={512}
            errorMessage={getFieldError('name')}
          />
        </IPStack.Item>
        <IPStack.Item>
          <TextInfo message={t('forms:Fields.TabNameInfo')}>
            <IPLabel>{t('forms:Fields.TabName')}</IPLabel>
          </TextInfo>
          <IPTextField
            disabled={props.metadataReadOnly}
            value={taskType.tabName}
            placeholder={t('forms:Fields.TabNamePlaceholder')}
            onChange={(ev, newValue) => {
              const newTaskType = taskType.clone();
              newTaskType.tabName = newValue?.trimStart() ?? '';
              validate('tabname', newTaskType);
              setTaskType(newTaskType);
            }}
            maxLength={32}
            errorMessage={getFieldError('tabname')}
          />
        </IPStack.Item>
        <IPStack.Item>
          <TextInfo message={t('forms:Fields.DescriptionInfo')}>
            <IPLabel>{t('forms:Fields.Description')}</IPLabel>
          </TextInfo>
          <IPTextField
            disabled={props.metadataReadOnly}
            value={taskType?.description ?? ''}
            multiline
            onChange={(ev, newValue) => {
              const newTaskType = taskType.clone();
              newTaskType.description = newValue ?? '';
              setTaskType(newTaskType);
            }}
            placeholder={t('forms:Fields.DescriptionPlaceholder')}
            maxLength={1024}
            style={{
              resize: 'vertical',
              maxHeight: 400,
            }}
          />
        </IPStack.Item>
        <IPStack.Item>
          <TextInfo message={t('forms:Fields.SystemTypeInfo')}>
            <IPLabel>{t('forms:Fields.SystemType')}</IPLabel>
          </TextInfo>
          <IPDropdown
            disabled={props.metadataReadOnly}
            options={getSystemTypeOptions()}
            styles={{ listbox: { maxHeight: 300 } }}
            selectedKey={taskType.systemTaskType ?? 0}
            onChange={(event: FormEvent<HTMLDivElement>, option?: IPSelectableOption) => {
              const newTaskType = taskType.clone();
              newTaskType.systemTaskType = option?.key ? (option?.key as number) : undefined;
              setTaskType(newTaskType);
            }}
          />
        </IPStack.Item>
        <IPStack.Item>
          <AuthSchemaPicker
            disabled={props.metadataReadOnly}
            selectedAuthSchemaId={taskType.authSchemaId}
            onSelect={(schema) => {
              const newTaskType = taskType.clone();
              newTaskType.authSchemaId = schema?.authSchemaId;
              setTaskType(newTaskType);
            }}
          />
        </IPStack.Item>
      </IPStack>
    </IPPanel>
  );
};
